import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, UserPlus, X, AlertTriangle, Mail, UserCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FriendPicker } from "@/components/FriendPicker";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMOJIS = ["🏠", "✈️", "🍕", "🎉", "💼", "🎓", "🌊", "🏖️", "🎬", "🎮", "🛍️", "🍻", "🍔", "☕", "🎂", "💸", "🚗", "🏔️", "👥"];

export const GroupSettingsDialog = ({ open, onOpenChange, group, currentUserId, onUpdated }) => {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("👥");
  const [saving, setSaving] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (group) { setName(group.name || ""); setEmoji(group.emoji || "👥"); }
    if (!open) { setConfirmingDelete(false); setDeleteConfirmText(""); }
  }, [group, open]);

  if (!group) return null;

  const isCreator = group.created_by === currentUserId;

  const saveBasics = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (name !== group.name) updates.name = name;
      if (emoji !== group.emoji) updates.emoji = emoji;
      if (Object.keys(updates).length === 0) { setSaving(false); return; }
      const { data } = await api.patch(`/groups/${group.id}`, updates);
      toast.success("Group updated");
      onUpdated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const inviteByEmail = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/members`, { email: inviteEmail });
      toast.success(`${data.name} added`);
      setInviteEmail("");
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setInviting(false); }
  };

  const addFriendToGroup = async (friend) => {
    try {
      await api.post(`/groups/${group.id}/members/by-id`, { user_id: friend.id });
      toast.success(`${friend.name} added`);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const removeMember = async (member) => {
    if (!window.confirm(`Remove ${member.name} from ${group.name}?`)) return;
    try {
      await api.delete(`/groups/${group.id}/members/${member.id}`);
      toast.success(`${member.name} removed`);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteGroup = async () => {
    if (deleteConfirmText !== group.name) { toast.error("Type the group name exactly"); return; }
    setDeleting(true);
    try {
      await api.delete(`/groups/${group.id}`);
      toast.success(`${group.name} deleted`);
      onOpenChange(false);
      nav("/groups");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Group settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general" className="mt-2">
          <TabsList className="bg-foreground/5 border border-border w-full grid grid-cols-4" data-testid="gs-tabs">
            <TabsTrigger value="general" data-testid="gs-tab-general">General</TabsTrigger>
            <TabsTrigger value="people" data-testid="gs-tab-people">People</TabsTrigger>
            <TabsTrigger value="members" data-testid="gs-tab-members">Members</TabsTrigger>
            <TabsTrigger value="danger" data-testid="gs-tab-danger" className="data-[state=active]:text-red-400">Danger</TabsTrigger>
          </TabsList>

          {/* General — rename + emoji */}
          <TabsContent value="general" className="mt-5 space-y-5">
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-muted border-border text-base" data-testid="gs-name" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Profile avatar</Label>
              <div className="flex flex-wrap gap-1.5">
                {EMOJIS.map((e) => (
                  <button
                    key={e} type="button" onClick={() => setEmoji(e)}
                    className={`h-11 w-11 rounded-md text-xl transition ${emoji === e ? "bg-foreground/10 ring-1 ring-[var(--brand)]" : "bg-foreground/[0.03] hover:bg-foreground/10"}`}
                    data-testid={`gs-emoji-${e}`}
                  >{e}</button>
                ))}
              </div>
            </div>
            <Button onClick={saveBasics} disabled={saving} className="brand-bg text-black hover:opacity-90 w-full h-11" data-testid="gs-save">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </TabsContent>

          {/* People — add via friends or email */}
          <TabsContent value="people" className="mt-5 space-y-5">
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5"><UserCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> From your friends</Label>
              <FriendPicker
                mode="add"
                existingMemberIds={group.member_ids}
                onAddInstant={addFriendToGroup}
              />
            </div>
            <form onSubmit={inviteByEmail} className="space-y-2 pt-4 border-t border-border">
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> Invite by email</Label>
              <div className="flex gap-2">
                <Input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="friend@example.com" className="bg-muted border-border text-base h-11" data-testid="gs-invite-email" />
                <Button type="submit" disabled={inviting} className="brand-bg text-black hover:opacity-90 h-11 shrink-0" data-testid="gs-invite-email-btn">
                  <UserPlus className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">They need an account first. Share the invite link from the group page for new users.</p>
            </form>
          </TabsContent>

          {/* Members list */}
          <TabsContent value="members" className="mt-5">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">{group.members?.length || 0} members</div>
            <div className="divide-y divide-border rounded-lg border border-border bg-foreground/[0.02]">
              {(group.members || []).map((m) => {
                const isThisCreator = m.id === group.created_by;
                const canRemove = (isCreator && !isThisCreator) || m.id === currentUserId;
                return (
                  <div key={m.id} className="flex items-center justify-between py-2.5 px-3" data-testid={`gs-member-${m.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-9 w-9 rounded-full bg-foreground/5 border border-border flex items-center justify-center text-base shrink-0">{m.avatar_emoji || "🙂"}</span>
                      <div className="min-w-0">
                        <div className="text-sm truncate">{m.name} {m.id === currentUserId && <span className="text-[10px] font-mono text-muted-foreground">(you)</span>}</div>
                        <div className="text-[11px] font-mono text-muted-foreground truncate">{isThisCreator ? "Group creator" : m.email}</div>
                      </div>
                    </div>
                    {canRemove ? (
                      <button onClick={() => removeMember(m)} className="h-9 w-9 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition flex items-center justify-center shrink-0" data-testid={`gs-remove-${m.id}`} aria-label="Remove member">
                        <X className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Owner</span>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Danger Zone */}
          <TabsContent value="danger" className="mt-5">
            {!isCreator ? (
              <div className="rounded-lg border border-border bg-foreground/[0.02] p-5 text-sm text-muted-foreground" data-testid="gs-not-owner">
                Only the group creator can delete this group. You can leave from the Members tab.
              </div>
            ) : !confirmingDelete ? (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-5 space-y-3" data-testid="gs-danger-zone">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="text-sm font-medium">Delete this group</div>
                    <div className="text-xs text-muted-foreground mt-1">All bills, balances, and settlement history will be permanently removed.</div>
                  </div>
                </div>
                <Button onClick={() => setConfirmingDelete(true)} variant="outline" className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-11" data-testid="gs-delete-init">
                  <Trash2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Delete group
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-5 space-y-4" data-testid="gs-delete-confirm">
                <div className="text-sm">
                  Type <span className="font-mono brand-text">{group.name}</span> to confirm. This <strong>cannot</strong> be undone.
                </div>
                <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder={group.name} className="bg-muted border-red-500/30 text-base h-11" autoFocus data-testid="gs-delete-confirm-input" />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => { setConfirmingDelete(false); setDeleteConfirmText(""); }} className="flex-1 border-border h-11" data-testid="gs-delete-cancel">Cancel</Button>
                  <Button onClick={deleteGroup} disabled={deleteConfirmText !== group.name || deleting} className="flex-1 bg-red-500 text-white hover:bg-red-600 h-11" data-testid="gs-delete-final">
                    {deleting ? "Deleting…" : "Delete forever"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
